import { Box, Chip, Grid, Paper, Stack, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import { DialogBody, DialogContainer, DialogTitle, GeneralTableBox, HeadTableCell, LoadingSkeleton, NoDataSkeleton, RowTableCell } from '../../../common/components';
import { useNotificationEvent } from '../../../hooks/useNotifications';
import { COMPLETED_PROJECT_TYPE, COUPON_COLLECTION_TYPE, COUPON_DUE_TYPE, MODULE_LABELS } from '../constants/notificationsConstants';
import { formatDateTime, statusChip } from '../utils/notificationUtils';

export const EventDetailDialog = ({ uuid, onClose }) => {
  const { data, isLoading } = useNotificationEvent(uuid);
  const context = data?.payload || {};
  const contract = context.contract || {};
  const project = context.project || {};
  const lot = context.lot || {};

  return (
    <DialogContainer open={Boolean(uuid)} onClose={onClose} size="medium" minHeight="auto">
      <DialogTitle>Detalle técnico</DialogTitle>
      <DialogBody sx={{ display: 'block' }}>
        {isLoading && <LoadingSkeleton />}
        {data && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">{data.title}</Typography>
              <Typography color="text.secondary">{data.message}</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {statusChip(data.status)}
              <Chip label={MODULE_LABELS[data.target_app_module] || data.target_app_module} size="small" />
              {data.is_urgent && <Chip label="Urgente" color="error" size="small" />}
              <Chip label={data.rule_name || data.rule_key} size="small" variant="outlined" />
            </Stack>
            {data.notification_type === COMPLETED_PROJECT_TYPE && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Datos operativos
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Contrato
                    </Typography>
                    <Typography>{contract.contract_number || data.contract_number || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Proyecto
                    </Typography>
                    <Typography>{project.name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Estado proyecto
                    </Typography>
                    <Typography>{project.status || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Finalización
                    </Typography>
                    <Typography>{project.end_date || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Lote / Manzana
                    </Typography>
                    <Typography>
                      {[lot.block_number, lot.lot_number].filter(Boolean).join(' / ') || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Loteo
                    </Typography>
                    <Typography>{lot.allotment_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Fecha poseedor
                    </Typography>
                    <Typography>{contract.current_possessor_date || 'Pendiente'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Seguro vida
                    </Typography>
                    <Typography>
                      {contract.life_insurance > 0 ? contract.life_insurance : 'Pendiente'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Faltantes
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(context.missing_labels || []).map((label) => (
                        <Chip key={label} label={label} size="small" color="warning" />
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            )}
            {data.notification_type === COUPON_DUE_TYPE && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Vencimiento de cupones
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Mes / año
                    </Typography>
                    <Typography>
                      {context.coupon_month || '-'} / {context.coupon_year || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Vencimiento
                    </Typography>
                    <Typography>{context.due_number ? `${context.due_number}°` : '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Fecha
                    </Typography>
                    <Typography>{context.due_date || context.target_date || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Cupones
                    </Typography>
                    <Typography>{context.coupon_count ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Destinatarios
                    </Typography>
                    <Typography>{context.subscriber_count ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Enviados
                    </Typography>
                    <Typography>{context.delivery_summary?.sent ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Fallidos
                    </Typography>
                    <Typography>{context.delivery_summary?.failed ?? 0}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Suprimidos / bloqueados
                    </Typography>
                    <Typography>
                      {(context.delivery_summary?.suppressed || 0) +
                        (context.delivery_summary?.blocked_non_production || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
            {data.notification_type === COUPON_COLLECTION_TYPE && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Cobros por vencimiento
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Período
                    </Typography>
                    <Typography>
                      {context.period_month || '-'} / {context.period_year || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Vencimiento
                    </Typography>
                    <Typography>{context.due_number ? `${context.due_number}°` : '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Porcentaje cobrado
                    </Typography>
                    <Typography>{context.collection_percentage ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Umbral esperado
                    </Typography>
                    <Typography>{context.expected_threshold ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Evaluables
                    </Typography>
                    <Typography>{context.evaluable_count ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Cobrados
                    </Typography>
                    <Typography>{context.collected_count ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Pendientes
                    </Typography>
                    <Typography>{context.pending_count ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Fuera de término
                    </Typography>
                    <Typography>{context.late_count ?? '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
            <Typography variant="subtitle2">Payload</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(data.payload || {}, null, 2)}
              </pre>
            </Paper>
            <Typography variant="subtitle2">Intentos</Typography>
            <GeneralTableBox>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Canal</HeadTableCell>
                    <HeadTableCell>Estado</HeadTableCell>
                    <HeadTableCell>Fecha</HeadTableCell>
                    <HeadTableCell>Error</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.attempts || []).length === 0 && (
                    <NoDataSkeleton colSpan={4} message="Sin intentos registrados." />
                  )}
                  {(data.attempts || []).map((attempt) => (
                    <TableRow key={attempt.uuid}>
                      <RowTableCell>{attempt.channel}</RowTableCell>
                      <RowTableCell>{statusChip(attempt.status)}</RowTableCell>
                      <RowTableCell>{formatDateTime(attempt.attempted_at)}</RowTableCell>
                      <RowTableCell>{attempt.error_message || '-'}</RowTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GeneralTableBox>
            <Typography variant="subtitle2">Destinatarios</Typography>
            <GeneralTableBox>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Tipo</HeadTableCell>
                    <HeadTableCell>Destinatario</HeadTableCell>
                    <HeadTableCell>Canal</HeadTableCell>
                    <HeadTableCell>Estado</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.recipients || []).length === 0 && (
                    <NoDataSkeleton colSpan={4} message="Sin destinatarios registrados." />
                  )}
                  {(data.recipients || []).map((recipient) => (
                    <TableRow key={recipient.uuid}>
                      <RowTableCell>{recipient.recipient_type}</RowTableCell>
                      <RowTableCell>{recipient.recipient_label || recipient.recipient_uuid || '-'}</RowTableCell>
                      <RowTableCell>{recipient.channel}</RowTableCell>
                      <RowTableCell>{statusChip(recipient.status)}</RowTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GeneralTableBox>
          </Stack>
        )}
      </DialogBody>
    </DialogContainer>
  );
};
